import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { BASE_URL } from '../apiConfig';

const buildQueryParams = (params) => {
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      searchParams.append(key, value.toString());
    }
  });
  return searchParams.toString();
};

export const fetchDailyCollection = createAsyncThunk(
  'collexa/fetchDailyCollection',
  async ({ emp_id, token, page = 1, limit = 10, ...searchParams }, { rejectWithValue }) => {
    try {
      const params = { page, limit, ...searchParams };
      const response = await axios.get(
        `${BASE_URL}collexa/daily/collection/?${buildQueryParams(params)}`,
        { 
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          } 
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || {
        message: 'Something went wrong. Please contact the IT team.'
      });
    }
  }
);

export const fetchOnctCollection = createAsyncThunk(
  'collexa/fetchOnctCollection',
  async ({ emp_id, token, page = 1, limit = 10, ...searchParams }, { rejectWithValue }) => {
    try {
      const params = { page, limit, ...searchParams };
      const response = await axios.get(
        `${BASE_URL}collexa/onct/collection/?${buildQueryParams(params)}`,
        { 
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          } 
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || {
        message: 'Something went wrong. Please contact the IT team.'
      });
    }
  }
);

export const fetchRejectedData = createAsyncThunk(
  'collexa/fetchRejectedData',
  async ({ token, page = 1, limit = 10, ...searchParams }, { rejectWithValue }) => {
    try {
      const params = { page, limit, ...searchParams };
      const response = await axios.get(
        `${BASE_URL}collexa/rejected/?${buildQueryParams(params)}`,
        { 
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          } 
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || {
        message: 'Something went wrong. Please contact the IT team.'
      });
    }
  }
)

export const fetchApprovedData = createAsyncThunk(
  'collexa/fetchApprovedData',
  async ({ token, page = 1, limit = 10, ...searchParams }, { rejectWithValue }) => {
    try {
      const params = { page, limit, ...searchParams };
      const response = await axios.get(
        `${BASE_URL}collexa/approved/?${buildQueryParams(params)}`,
        { 
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          } 
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || {
        message: 'Something went wrong. Please contact the IT team.'
      });
    }
  }
)

export const fetchPendingHoApproval = createAsyncThunk(
  'collexa/fetchPendingHoApproval',
  async ({ token, page = 1, limit = 10, ...searchParams }, { rejectWithValue }) => {
    try {
      const params = { page, limit, ...searchParams };
      const response = await axios.get(
        `${BASE_URL}collexa/pending/ho-approval/?${buildQueryParams(params)}`,
        { 
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          } 
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || {
        message: 'Something went wrong. Please contact the IT team.'
      });
    }
  }
)

export const fetchPendingBranchApproval = createAsyncThunk(
  'collexa/fetchPendingBranchApproval',
  async ({ token, page = 1, limit = 10, ...searchParams }, { rejectWithValue }) => {
    try {
      const params = { page, limit, ...searchParams };
      const response = await axios.get(
        `${BASE_URL}collexa/pending/branch-approval/?${buildQueryParams(params)}`,
        { 
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          } 
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || {
        message: 'Something went wrong. Please contact the IT team.'
      });
    }
  }
)

export const fetchPendingForCollection = createAsyncThunk(
  'collexa/fetchPendingForCollection',
  async ({ token, page = 1, limit = 10, ...searchParams }, { rejectWithValue }) => {
    try {
      const params = { page, limit, ...searchParams };
      const response = await axios.get(
        `${BASE_URL}collexa/pending/for-collection/?${buildQueryParams(params)}`,
        { 
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          } 
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || {
        message: 'Something went wrong. Please contact the IT team.'
      });
    }
  }
)

const formatDate = () => {
  const options = { day: '2-digit', month: 'short' };
  const date = new Date().toLocaleDateString('en-GB', options);
  return date.replace(/ /g, '-');
};


export const downloadCollexaReport = createAsyncThunk(
  'collexa/downloadCollexaReport',
  async ({ token, type }, { rejectWithValue }) => {
    try {
      const response = await fetch(`${BASE_URL}collexa/download/report/?type=${type}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      link.download = `${type}_${formatDate()}.xlsx`;
      link.click();

      URL.revokeObjectURL(url);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const initialState = {
  dailyCollectionLoading: false,
  dailyCollectionError: null,
  dailyCollectionData: [],
  paginationDataDailyCollection: {
    page: 1,
    limit: 10,
    total_pages: 0,
    total_count: 0
  },

  // fetch reject
  rejectedLoading: false,
  rejectedError: null,
  rejectedData: [],
  paginationDataRejected: {
    page: 1,
    limit: 10,
    total_pages: 0,
    total_count: 0
  },

  // fetch approved
  approvedLoading: false,
  approvedError: null,
  approvedData: [],
  paginationDataApproved: {
    page: 1,
    limit: 10,
    total_pages: 0,
    total_count: 0
  },

  // fetch pending ho approval
  pendingHoApprovalLoading: false,
  pendingHoApprovalError: null,
  pendingHoApprovalData: [],
  paginationDataPendingHoApproval: {
    page: 1,
    limit: 10,
    total_pages: 0,
    total_count: 0
  },

  // fetch pending branch approval
  pendingBranchApprovalLoading: false,
  pendingBranchApprovalError: null,
  pendingBranchApprovalData: [],
  paginationDataPendingBranchApproval: {
    page: 1,
    limit: 10,
    total_pages: 0,
    total_count: 0
  },

  // fetch pending for collection
  pendingForCollectionLoading: false,
  pendingForCollectionError: null,
  pendingForCollectionData: [],
  paginationDataPendingForCollection: {
    page: 1,
    limit: 10,
    total_pages: 0,
    total_count: 0
  },
  onctCollectionLoading: false,
  onctCollectionError: null,
  onctCollectionData: [],
  paginationDataOnctCollection: {
    page: 1,
    limit: 10,
    total_pages: 0,
    total_count: 0
  }
};

const receiptsSlice = createSlice({
  name: 'collexa',
  initialState,
  reducers: {
    resetDailyCollectionState: (state) => {
      state.dailyCollectionLoading = false;
      state.dailyCollectionError = null;
      state.dailyCollectionData = [];
      state.paginationDataDailyCollection = {
        page: 1,
        limit: 10,
        total_pages: 0,
        total_count: 0
      };
    },
    resetOnctCollectionState: (state) => {
      state.onctCollectionLoading = false;
      state.onctCollectionError = null;
      state.onctCollectionData = [];
      state.paginationDataOnctCollection = {
        page: 1,
        limit: 10,
        total_pages: 0,
        total_count: 0
      };
    }
  },
  extraReducers: (builder) => {
    builder
      // Daily Collection cases
      .addCase(fetchDailyCollection.pending, (state) => {
        state.dailyCollectionLoading = true;
        state.dailyCollectionError = null;
      })
      .addCase(fetchDailyCollection.fulfilled, (state, action) => {
        state.dailyCollectionLoading = false;
        state.dailyCollectionData = action.payload.data.daily_collection_data || [];
        state.paginationDataDailyCollection = {
          page: action.payload.data.pagination?.page || 1,
          limit: action.payload.data.pagination?.limit || 10,
          total_pages: action.payload.data.pagination?.total_pages || 0,
          total_count: action.payload.data.pagination?.total_count || 0
        };
      })
      .addCase(fetchDailyCollection.rejected, (state, action) => {
        state.dailyCollectionLoading = false;
        state.dailyCollectionError = action.payload?.message || 
          action.payload?.error?.message || 
          'Failed to fetch daily collection data';
      })
      
      // ONCT Collection cases
      .addCase(fetchOnctCollection.pending, (state) => {
        state.onctCollectionLoading = true;
        state.onctCollectionError = null;
      })
      .addCase(fetchOnctCollection.fulfilled, (state, action) => {
        state.onctCollectionLoading = false;
        state.onctCollectionData = action.payload.data.onct_collection_data || [];
        state.paginationDataOnctCollection = {
          page: action.payload.data.pagination?.page || 1,
          limit: action.payload.data.pagination?.limit || 10,
          total_pages: action.payload.data.pagination?.total_pages || 0,
          total_count: action.payload.data.pagination?.total_count || 0
        };
      })
      .addCase(fetchOnctCollection.rejected, (state, action) => {
        state.onctCollectionLoading = false;
        state.onctCollectionError = action.payload?.message || 
          action.payload?.error?.message || 
          'Failed to fetch ONCT collection data';
      })

      // fetch fetchRejectedData
      .addCase(fetchRejectedData.pending, (state) => {
        state.rejectedLoading = true;
        state.rejectedError = null;
      })
      .addCase(fetchRejectedData.fulfilled, (state, action) => {
        state.rejectedLoading = false;
        state.rejectedData = action.payload.data.rejected_data || [];
        // console.log("state.rejectedData", state.rejectedData)
        state.paginationDataRejected = {
          page: action.payload.data.pagination?.page || 1,
          limit: action.payload.data.pagination?.limit || 10,
          total_pages: action.payload.data.pagination?.total_pages || 0,
          total_count: action.payload.data.pagination?.total_count || 0
        }
      })
      .addCase(fetchRejectedData.rejected, (state, action) => {
        state.rejectedLoading = false;
        state.rejectedError = action.payload?.message || 
          action.payload?.error?.message || 
          'Failed to fetch rejected data';
      })

      // fetch fetchApprovedData
      .addCase(fetchApprovedData.pending, (state) => {
        state.approvedLoading = true;
        state.approvedError = null;
      })
      .addCase(fetchApprovedData.fulfilled, (state, action) => {
        state.approvedLoading = false;
        state.approvedData = action.payload.data.approved_data || [];
        state.paginationDataApproved = {
          page: action.payload.data.pagination?.page || 1,
          limit: action.payload.data.pagination?.limit || 10,
          total_pages: action.payload.data.pagination?.total_pages || 0,
          total_count: action.payload.data.pagination?.total_count || 0
        }
      })
      .addCase(fetchApprovedData.rejected, (state, action) => {
        state.approvedLoading = false;
        state.approvedError = action.payload?.message || 
          action.payload?.error?.message || 
          'Failed to fetch approved data';
      })

      // fetch branch approval data
      .addCase(fetchPendingBranchApproval.pending, (state) => {
        state.pendingBranchApprovalLoading = true;
        state.pendingBranchApprovalError = null;
      })
      .addCase(fetchPendingBranchApproval.fulfilled, (state, action) => {
        state.pendingBranchApprovalLoading = false;
        state.pendingBranchApprovalData = action.payload.data.pending_branch_approval_data || [];
        console.log("state.pendingBranchApprovalData", state.pendingBranchApprovalData)
        state.paginationDataPendingBranchApproval = {
          page: action.payload.data.pagination?.page || 1,
          limit: action.payload.data.pagination?.limit || 10,
          total_pages: action.payload.data.pagination?.total_pages || 0,
          total_count: action.payload.data.pagination?.total_count || 0
        };
      })
      .addCase(fetchPendingBranchApproval.rejected, (state, action) => {
        state.pendingBranchApprovalLoading = false;
        state.pendingBranchApprovalError = action.payload?.message || 
          action.payload?.error?.message || 
          'Failed to fetch branch approval data';
      })
      

      // fetch pending ho approval
     .addCase(fetchPendingHoApproval.pending, (state) => {
        state.pendingHoApprovalLoading = true;
        state.pendingHoApprovalError = null;
      })
      .addCase(fetchPendingHoApproval.fulfilled, (state, action) => {
        state.pendingHoApprovalLoading = false;
        state.pendingHoApprovalData = action.payload.data.pending_ho_approval_data || [];
        console.log("state.pendingHoApprovalData", state.pendingHoApprovalData)
        state.paginationDataPendingHoApproval = {
          page: action.payload.data.pagination?.page || 1,
          limit: action.payload.data.pagination?.limit || 10,
          total_pages: action.payload.data.pagination?.total_pages || 0,
          total_count: action.payload.data.pagination?.total_count || 0
        };
      })
      .addCase(fetchPendingHoApproval.rejected, (state, action) => {
        state.pendingHoApprovalLoading = false;
        state.pendingHoApprovalError = action.payload?.message || 
          action.payload?.error?.message || 
          'Failed to fetch ho approval data';
      })
    

      // fetch pending for collection
      .addCase(fetchPendingForCollection.pending, (state) => {
        state.pendingForCollectionLoading = true;
        state.pendingForCollectionError = null;
      })
      .addCase(fetchPendingForCollection.fulfilled, (state, action) => {
        state.pendingForCollectionLoading = false;
        state.pendingForCollectionData = action.payload.data.pending_for_collection_data || [];
        console.log("state.pendingForCollectionData", state.pendingForCollectionData)
        state.paginationDataPendingForCollection = {
          page: action.payload.data.pagination?.page || 1,
          limit: action.payload.data.pagination?.limit || 10,
          total_pages: action.payload.data.pagination?.total_pages || 0,
          total_count: action.payload.data.pagination?.total_count || 0
        };
      })
      .addCase(fetchPendingForCollection.rejected, (state, action) => {
        state.pendingForCollectionLoading = false;
        state.pendingForCollectionError = action.payload?.message || 
          action.payload?.error?.message || 
          'Failed to fetch pending for collection data';
      })
  }
});

export const { 
  resetDailyCollectionState,
  resetOnctCollectionState 
} = receiptsSlice.actions;

export default receiptsSlice.reducer;